import {
  extractNotionIdFromFilename,
  normalizeExportPath,
  titleFromExportFilename,
} from './parse-filename.js';
import type { NotionExportNode } from './types.js';

export type ExportFileIndex = Map<string, string>;
export type DatabaseCsvIndex = Map<string, string>;

/**
 * Map Notion page id (from filename suffix) to normalized .md path.
 * Used when index.html href path resolution fails (e.g. HTML entity mismatch).
 */
export function indexMdByNotionId(paths: Iterable<string>): Map<string, string> {
  const index = new Map<string, string>();
  for (const p of paths) {
    if (!/\.md$/i.test(p)) {
      continue;
    }
    const norm = normalizeExportPath(p);
    const filename = norm.split('/').pop() ?? norm;
    const notionId = extractNotionIdFromFilename(filename);
    if (notionId) {
      index.set(notionId, norm);
    }
  }
  return index;
}

export function indexMdPaths(paths: Iterable<string>): ExportFileIndex {
  const index = new Map<string, string>();
  for (const p of paths) {
    if (!/\.md$/i.test(p)) {
      continue;
    }
    index.set(normalizeExportPath(p), p);
  }
  return index;
}

/**
 * Notion database table exports keyed by collection id. Notion emits two CSVs
 * per database: `Title {hex}.csv` (current view) and `Title {hex}_all.csv`
 * (every row). We prefer the `_all` variant so filtered views still import fully.
 */
export function indexDatabaseCsvExports(paths: Iterable<string>): DatabaseCsvIndex {
  const index = new Map<string, string>();
  for (const p of paths) {
    if (!/\.csv$/i.test(p)) {
      continue;
    }
    const norm = normalizeExportPath(p);
    const filename = norm.split('/').pop() ?? norm;
    const notionId = extractNotionIdFromFilename(filename);
    if (!notionId) {
      continue;
    }
    const isAll = /_all\.csv$/i.test(filename);
    const existing = index.get(notionId);
    if (!existing || isAll) {
      index.set(notionId, norm);
    }
  }
  return index;
}

export function collectNodes(
  roots: NotionExportNode[],
  selectedNotionIds: Set<string>,
): NotionExportNode[] {
  const out: NotionExportNode[] = [];

  function walk(node: NotionExportNode, ancestorSelected: boolean): void {
    const id = node.notionId;
    const selected = ancestorSelected || (id != null && selectedNotionIds.has(id));
    if (selected && node.href) {
      out.push(node);
    }
    for (const child of node.children) {
      walk(child, selected);
    }
  }

  for (const root of roots) {
    walk(root, false);
  }
  return out;
}

export function countDescendants(node: NotionExportNode): number {
  let n = node.href ? 1 : 0;
  for (const child of node.children) {
    n += countDescendants(child);
  }
  return n;
}

export function databaseTitleFromCsvPath(csvPath: string): string {
  return titleFromExportFilename(csvPath.split('/').pop() ?? csvPath);
}
